/**
 * TimelineTemplate — ATS-Optimized | Single-Column | Vertical Timeline Entries
 *
 * Design Identity:
 *   - Centered header: name large, role in accent below, 2 contact rows
 *   - Section headings: accent-colored text, full-width rule below
 *   - Experience / Internship entries: left vertical timeline rule (2px accent)
 *     with a filled circle marker dot at entry start — gives a natural
 *     "career story" progression feel
 *   - Projects, Education, Certs, Achievements: clean without timeline rule
 *   - Zero images, zero graphics → 100% ATS-safe, pure text
 *   - Font: Georgia serif for warmth; clean and readable on screen + print
 *
 * All Sections Supported:
 *   Summary · Skills · Experience · Internships · Projects ·
 *   Education · Certifications · Achievements · Extracurricular ·
 *   Interests · Hobbies · Languages
 */
import { normalizeTemplateData, safePoint, ResumeLink } from './templateHelpers';

function TimelineTemplate({ resumeData, accentColor = '#1a56a0' }) {
    if (!resumeData) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', padding: '60px 32px', flexDirection: 'column', textAlign: 'center' }}>
                <p style={{ color: '#94a3b8', fontSize: 13, fontWeight: 600 }}>Chat with AI to build your resume</p>
            </div>
        );
    }

    const d = normalizeTemplateData(resumeData);
    const a = accentColor;

    /* ── Typography tokens ── */
    const SERIF = "Georgia, 'Times New Roman', Times, serif";
    const SANS  = "'Inter', 'Helvetica Neue', Arial, sans-serif";
    const FS    = '10.5px';
    const LH    = 1.54;
    const DARK  = '#111827';
    const MID   = '#374151';
    const LIGHT = '#6b7280';

    /* ── Skill categories ── */
    // Prefer user-defined categories from the form editor; fall back to auto-categorisation
    let skillCategories;
    if (Array.isArray(d.skillCategories) && d.skillCategories.length > 0) {
        skillCategories = d.skillCategories
            .filter(c => Array.isArray(c.skills) && c.skills.length > 0)
            .map(c => ({ label: c.name, items: c.skills }));
    } else {
        skillCategories = [];
        if (d.skills.length > 0) {
            const langs      = d.skills.filter(s => /\b(java|python|javascript|typescript|php|c\+\+|c#|kotlin|swift|golang|ruby|sql|html|css|bash|shell|scala|perl|dart)\b/i.test(s));
            const frameworks = d.skills.filter(s => /spring|react|next\.js|angular|vue|django|flask|fastapi|node|express|bootstrap|laravel|rails|asp\.net/i.test(s));
            const databases  = d.skills.filter(s => /mysql|mongodb|postgres|sqlite|redis|oracle|firebase|dynamodb|nosql|rest\s*api|graphql|api/i.test(s));
            const infra      = d.skills.filter(s => /linux|nginx|cloudflare|docker|kubernetes|aws|azure|gcp|vercel|git\b|github|ci\/cd|jenkins|terraform|ssh/i.test(s));
            const practices  = d.skills.filter(s => /agile|scrum|oop|tdd|unit\s*test|code\s*review|debugging|postman|jira|kanban/i.test(s));
            const ai         = d.skills.filter(s => /\b(ai|ml|machine\s*learning|gpt|llm|openai|tensorflow|pytorch|nlp)\b/i.test(s));

            const assigned = new Set([...langs, ...frameworks, ...databases, ...infra, ...practices, ...ai].map(s => s.toLowerCase()));
            const other    = d.skills.filter(s => !assigned.has(s.toLowerCase()));

            if (langs.length)      skillCategories.push({ label: 'Languages',               items: langs });
            if (frameworks.length) skillCategories.push({ label: 'Frameworks & Libraries',  items: frameworks });
            if (databases.length)  skillCategories.push({ label: 'Databases & APIs',        items: databases });
            if (infra.length)      skillCategories.push({ label: 'Infrastructure & DevOps', items: infra });
            if (practices.length)  skillCategories.push({ label: 'Engineering Practices',   items: practices });
            if (ai.length)         skillCategories.push({ label: 'AI & ML',                 items: ai });
            if (other.length)      skillCategories.push({ label: 'Other Skills',             items: other });
            if (skillCategories.length === 0) skillCategories.push({ label: 'Skills', items: d.skills });
        }
    }


    /* ── Contact rows ── */
    const line1 = [];
    if (d.city)  line1.push(d.city);
    if (d.email) line1.push(d.email);
    if (d.phone) line1.push(d.phone.startsWith('+91') ? d.phone : `+91 ${d.phone}`);

    const stripUrl = url => url ? url.replace(/^https?:\/\/(www\.)?/, '') : '';
    const linkItems = [];
    if (d.links?.linkedin)  linkItems.push({ href: d.links.linkedin,  label: stripUrl(d.links.linkedin) });
    if (d.links?.github)    linkItems.push({ href: d.links.github,    label: stripUrl(d.links.github) });
    if (d.links?.portfolio) linkItems.push({ href: d.links.portfolio, label: stripUrl(d.links.portfolio) });

    /* ── Helpers ── */
    const intersperse = arr => arr.reduce((acc, el, i) =>
        i === 0 ? [el] : [...acc, <span key={`d${i}`} style={{ margin: '0 5px', color: LIGHT }}>·</span>, el], []);

    /* ── Section heading ── */
    const SH = ({ title }) => (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '18px',
            marginBottom: '8px',
        }}>
            <span style={{
                fontSize: '11.5px',
                fontWeight: 700,
                color: a,
                fontFamily: SERIF,
                letterSpacing: '0.01em',
                lineHeight: '1.35',
                whiteSpace: 'nowrap',
            }}>
                {title}
            </span>
            <div style={{ flex: 1, height: '1.5px', background: a, opacity: 0.35 }} />
        </div>
    );

    /* ── Bullet list item ── */
    const liStyle = {
        position: 'relative',
        paddingLeft: '14px',
        marginBottom: '2px',
        fontSize: FS,
        color: MID,
        lineHeight: LH,
        listStyle: 'none',
        fontFamily: SERIF,
    };
    const Dot = () => (
        <span style={{ position: 'absolute', left: '2px', top: 0, color: MID, lineHeight: LH }}>•</span>
    );

    /* ── Simple bullet list ── */
    const BList = ({ items }) => (
        <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
            {items.map((item, i) => (
                <li key={i} style={liStyle}>
                    <Dot />
                    {typeof item === 'string' ? item : safePoint(item)}
                </li>
            ))}
        </ul>
    );

    /*
     * ── Timeline Entry component ──
     * Renders: [ circle ] Role ──────────── Date
     *          [ line  ] Company (italic)
     *          [ line  ] • bullet
     *          [ line  ] • bullet
     * The vertical line is a 2px left border on the content div,
     * offset to align with the circle center.
    */
    const CIRCLE_W = 10;  /* px, diameter of timeline dot */
    const LINE_X   = 4;   /* px, circle center from left edge */

    const TimelineEntry = ({ role, company, duration, points, description }) => (
        <div style={{ display: 'flex', gap: 0, marginBottom: '2px' }}>
            {/* Left rail: dot + vertical line */}
            <div style={{ width: `${CIRCLE_W + 8}px`, flexShrink: 0, position: 'relative', paddingTop: '2px' }}>
                {/* Circle */}
                <div style={{
                    width: `${CIRCLE_W}px`,
                    height: `${CIRCLE_W}px`,
                    borderRadius: '50%',
                    background: a,
                    position: 'absolute',
                    top: '3px',
                    left: 0,
                    zIndex: 1,
                }} />
                {/* Vertical line below circle */}
                <div style={{
                    position: 'absolute',
                    top: `${3 + CIRCLE_W}px`,
                    left: `${LINE_X - 1}px`,
                    width: '2px',
                    bottom: '-2px',
                    background: `${a}40`,   /* accent at 25% opacity */
                }} />
            </div>

            {/* Content */}
            <div style={{ flex: 1, paddingBottom: '10px' }}>
                {/* Role + Date row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: DARK, lineHeight: 1.35, fontFamily: SERIF }}>
                        {role}
                    </span>
                    {duration && (
                        <span style={{ fontSize: '10px', color: LIGHT, fontStyle: 'italic', whiteSpace: 'nowrap', flexShrink: 0, lineHeight: 1.35, fontFamily: SERIF }}>
                            {duration}
                        </span>
                    )}
                </div>
                {/* Company */}
                {company && (
                    <div style={{ fontSize: '10.5px', color: LIGHT, fontStyle: 'italic', marginTop: '1px', marginBottom: '2px', fontFamily: SERIF }}>
                        {company}
                    </div>
                )}
                {/* Bullet points */}
                {points?.length > 0 && (
                    <ul style={{ margin: '2px 0 0', padding: 0, listStyle: 'none' }}>
                        {points.map((pt, j) => (
                            <li key={j} style={liStyle}>
                                <Dot />
                                {safePoint(pt)}
                            </li>
                        ))}
                    </ul>
                )}
                {/* Description fallback */}
                {!points?.length && description && (
                    <div style={{ ...liStyle, marginTop: '2px' }}>
                        <Dot />
                        {description}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div id="resume" style={{ fontFamily: SERIF, background: '#fff', color: DARK, fontSize: FS, lineHeight: LH }}>

            {/* ══════════════════════════════════════════
                HEADER — centered, clean
            ══════════════════════════════════════════ */}
            <div style={{ textAlign: 'center', padding: '20px 32px 12px' }}>
                <h1 style={{
                    fontSize: '23px',
                    fontWeight: 700,
                    color: DARK,
                    margin: 0,
                    fontFamily: SERIF,
                    letterSpacing: '0.01em',
                    lineHeight: 1.2,
                }}>
                    {d.name || 'Your Name'}
                </h1>

                {d.role && (
                    <p style={{ fontSize: '11px', color: a, margin: '4px 0 0', fontWeight: 600, fontFamily: SANS, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                        {d.role}
                    </p>
                )}

                {/* Thin accent rule under name */}
                <div style={{ width: '50px', height: '2px', background: a, margin: '8px auto 0', borderRadius: '2px' }} />

                {/* Contact line 1 */}
                {line1.length > 0 && (
                    <p style={{ fontSize: '9.5px', color: MID, margin: '8px 0 0', lineHeight: 1.5, fontFamily: SANS }}>
                        {intersperse(line1)}
                    </p>
                )}

                {/* Contact line 2 — links */}
                {linkItems.length > 0 && (
                    <p style={{ fontSize: '9.5px', margin: '2px 0 0', lineHeight: 1.5, fontFamily: SANS }}>
                        {intersperse(linkItems.map((lp, i) => (
                            <ResumeLink
                                key={`lp${i}`}
                                href={lp.href}
                                label={lp.label}
                                style={{ color: a, fontSize: '9.5px' }}
                            />
                        )))}
                    </p>
                )}
            </div>

            {/* ══════════════════════════════════════════
                BODY
            ══════════════════════════════════════════ */}
            <div style={{ padding: '2px 32px 24px' }}>

                {/* ── Dynamic section ordering via SECTION_RENDERERS ── */}
                {(() => {
                    const SECTION_RENDERERS = {
                        summary: () => d.summaryText ? (
                            <section key="summary">
                                <SH title="Professional Summary" />
                                <p style={{ fontSize: FS, color: MID, lineHeight: LH, margin: 0, textAlign: 'justify', fontFamily: SERIF }}>
                                    {d.summaryText}
                                </p>
                            </section>
                        ) : null,

                        skills: () => skillCategories.length > 0 ? (
                            <section key="skills">
                                <SH title="Technical Skills" />
                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: FS, fontFamily: SERIF }}>
                                    <tbody>
                                        {skillCategories.map((cat, i) => (
                                            <tr key={i}>
                                                <td style={{
                                                    fontWeight: 700,
                                                    color: DARK,
                                                    fontSize: FS,
                                                    paddingRight: '8px',
                                                    paddingBottom: '2px',
                                                    whiteSpace: 'nowrap',
                                                    verticalAlign: 'top',
                                                    width: '30%',
                                                    lineHeight: LH,
                                                }}>
                                                    {cat.label}:
                                                </td>
                                                <td style={{ fontSize: FS, color: MID, lineHeight: LH, paddingBottom: '2px', verticalAlign: 'top' }}>
                                                    {cat.items.join(', ')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </section>
                        ) : null,

                        experience: () => d.experience.length > 0 ? (
                            <section key="experience">
                                <SH title="Professional Experience" />
                                <div>
                                    {d.experience.map((exp, i) => (
                                        <TimelineEntry
                                            key={i}
                                            role={exp.role}
                                            company={exp.company}
                                            duration={exp.duration}
                                            points={exp.points}
                                        />
                                    ))}
                                </div>
                            </section>
                        ) : null,

                        internships: () => d.internships.length > 0 ? (
                            <section key="internships">
                                <SH title="Internships" />
                                <div>
                                    {d.internships.map((intern, i) => (
                                        <TimelineEntry
                                            key={i}
                                            role={intern.role || intern.title || intern.position || 'Intern'}
                                            company={intern.company || intern.organization}
                                            duration={intern.duration || intern.period}
                                            points={intern.points}
                                            description={intern.description}
                                        />
                                    ))}
                                </div>
                            </section>
                        ) : null,

                        projects: () => d.projects.length > 0 ? (
                            <section key="projects">
                                <SH title="Projects" />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                                    {d.projects.map((proj, i) => (
                                        <div key={i}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', flexWrap: 'wrap' }}>
                                                <div style={{ display: 'flex', gap: '8px', alignItems: 'baseline', flexWrap: 'wrap', flex: 1 }}>
                                                    <span style={{ fontSize: '11px', fontWeight: 700, color: DARK, fontStyle: 'italic', fontFamily: SERIF }}>
                                                        {proj.title || proj.name}
                                                    </span>
                                                    {proj.tech?.length > 0 && (
                                                        <span style={{ fontSize: '10px', color: a, fontStyle: 'italic', fontFamily: SERIF }}>
                                                            {Array.isArray(proj.tech) ? proj.tech.join(', ') : proj.tech}
                                                        </span>
                                                    )}
                                                </div>
                                                {(proj.impact || proj.status) && (
                                                    <span style={{ fontSize: '9.5px', color: LIGHT, fontWeight: 600, fontStyle: 'italic', whiteSpace: 'nowrap', flexShrink: 0, fontFamily: SERIF }}>
                                                        {proj.impact || proj.status}
                                                    </span>
                                                )}
                                            </div>
                                            {proj.description && (
                                                <div style={{ ...liStyle, marginTop: '2px' }}>
                                                    <Dot />
                                                    {proj.description}
                                                </div>
                                            )}
                                            {proj.points?.length > 0 && (
                                                <ul style={{ margin: '2px 0 0', padding: 0, listStyle: 'none' }}>
                                                    {proj.points.map((pt, j) => (
                                                        <li key={j} style={liStyle}>
                                                            <Dot />
                                                            {safePoint(pt)}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ) : null,

                        education: () => d.educationList.length > 0 ? (
                            <section key="education">
                                <SH title="Education" />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                                    {d.educationList.map((edu, i) => (
                                        <div key={i}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                                <span style={{ fontSize: '11px', fontWeight: 700, color: DARK, lineHeight: 1.35, fontFamily: SERIF }}>
                                                    {edu?.degree}
                                                </span>
                                                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                    <span style={{ fontSize: '10px', color: LIGHT, fontStyle: 'italic', fontFamily: SERIF }}>
                                                        {edu?.year}
                                                    </span>
                                                    {edu?.cgpa && (
                                                        <span style={{ fontSize: '10px', color: LIGHT, marginLeft: '7px', fontFamily: SERIF }}>
                                                            {edu.cgpa}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {edu?.college && (
                                                <div style={{ fontSize: '10.5px', color: LIGHT, fontStyle: 'italic', marginTop: '1px', fontFamily: SERIF }}>
                                                    {edu.college}
                                                </div>
                                            )}
                                            {edu?.specialization && (
                                                <p style={{ fontSize: '10px', color: LIGHT, margin: '1px 0 0', lineHeight: LH, fontFamily: SERIF }}>
                                                    <em>Relevant Coursework: </em>{edu.specialization}
                                                </p>
                                            )}
                                            {!edu?.specialization && edu?.description && (
                                                <p style={{ fontSize: '10px', color: LIGHT, margin: '1px 0 0', lineHeight: LH, fontStyle: 'italic', fontFamily: SERIF }}>
                                                    {edu.description}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ) : null,

                        certifications: () => d.certList.length > 0 ? (
                            <section key="certifications">
                                <SH title="Certifications" />
                                <BList items={d.certList} />
                            </section>
                        ) : null,

                        achievements: () => d.achieveList.length > 0 ? (
                            <section key="achievements">
                                <SH title="Achievements" />
                                <BList items={d.achieveList} />
                            </section>
                        ) : null,

                        extracurricular: () => d.extraList.length > 0 ? (
                            <section key="extracurricular">
                                <SH title="Extracurricular Activities" />
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                                    {d.extraList.map((item, i) => {
                                        if (typeof item === 'string') {
                                            return (
                                                <div key={i} style={liStyle}>
                                                    <Dot />
                                                    {item}
                                                </div>
                                            );
                                        }
                                        return (
                                            <div key={i}>
                                                {(item.title || item.role) && (
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                                                        <span style={{ fontSize: '11px', fontWeight: 700, color: DARK, lineHeight: 1.35, fontFamily: SERIF }}>
                                                            {item.title || item.role}
                                                        </span>
                                                        {(item.duration || item.period) && (
                                                            <span style={{ fontSize: '10px', color: LIGHT, fontStyle: 'italic', whiteSpace: 'nowrap', flexShrink: 0, fontFamily: SERIF }}>
                                                                {item.duration || item.period}
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                                {(item.organization || item.club) && (
                                                    <div style={{ fontSize: '10.5px', color: LIGHT, fontStyle: 'italic', marginTop: '1px', fontFamily: SERIF }}>
                                                        {item.organization || item.club}
                                                    </div>
                                                )}
                                                {item.description && (
                                                    <div style={{ ...liStyle, marginTop: '1px' }}>
                                                        <Dot />
                                                        {item.description}
                                                    </div>
                                                )}
                                                {item.points?.length > 0 && <BList items={item.points} />}
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        ) : null,

                        interests: () => d.interests.length > 0 ? (
                            <section key="interests">
                                <SH title="Interests" />
                                <p style={{ fontSize: FS, color: MID, margin: 0, lineHeight: LH, fontFamily: SERIF }}>
                                    {d.interests.join('  ·  ')}
                                </p>
                            </section>
                        ) : null,

                        hobbies: () => d.hobbies.length > 0 ? (
                            <section key="hobbies">
                                <SH title="Hobbies" />
                                <p style={{ fontSize: FS, color: MID, margin: 0, lineHeight: LH, fontFamily: SERIF }}>
                                    {d.hobbies.join('  ·  ')}
                                </p>
                            </section>
                        ) : null,

                        languages: () => d.languageList.length > 0 ? (
                            <section key="languages">
                                <SH title="Languages" />
                                <p style={{ fontSize: FS, color: MID, margin: 0, lineHeight: LH, fontFamily: SERIF }}>
                                    {d.languageList.join('  ·  ')}
                                </p>
                            </section>
                        ) : null,
                    };

                    const DEFAULT_ORDER = ['summary', 'skills', 'experience', 'internships', 'projects', 'education', 'certifications', 'achievements', 'extracurricular', 'interests', 'hobbies', 'languages'];
                    const savedOrder = Array.isArray(resumeData.section_order) && resumeData.section_order.length > 0
                        ? resumeData.section_order
                        : DEFAULT_ORDER;
                    const orderedKeys = [
                        ...savedOrder.filter(k => SECTION_RENDERERS[k]),
                        ...DEFAULT_ORDER.filter(k => SECTION_RENDERERS[k] && !savedOrder.includes(k)),
                    ];

                    return orderedKeys.map(key => SECTION_RENDERERS[key] ? SECTION_RENDERERS[key]() : null);
                })()}

            </div>
        </div>
    );
}

export default TimelineTemplate;
